if (!customElements.get('product-buybox')) {
  class ProductBuybox extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form[data-type="add-to-cart-form"]');
      this.sectionId = this.closest('[data-section-id]').dataset.sectionId;

      this.options = this.querySelectorAll('[data-option]');
      this.variantIdInput = this.querySelector('[data-variant-id]');

      this.quantityInput = this.querySelector('[data-quantity-selector]');
      this.quantityAmount = this.quantityInput?.querySelector('[data-quantity-input]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.price = this.querySelector('[data-price]');
      this.compareAtPrice = this.querySelector('[data-compare-at-price]');
      this.sku = this.querySelector('[data-sku]');
      this.stickyAtc = this.closest('[data-product')?.querySelector('product-sticky-atc');
      this.preOrderForm = this.closest('[data-product')?.querySelector('pre-order-form');
      this.bundleForm = this.closest('[data-product')?.querySelector('bundle-form');

      this.inventoryGauge = this.querySelector('[data-inventory-gauge]');
      this.inventoryMessage = this.inventoryGauge?.querySelector('[data-inventory-message]');
      this.inventoryIcon = this.inventoryGauge?.querySelector('[data-inventory-icon]');
      this.highRiskThreshold = this.inventoryGauge?.dataset.highRiskThreshold;
      this.mediumRiskThreshold = this.inventoryGauge?.dataset.mediumRiskThreshold;
      this.productShippingDetails = document.querySelector('product-shipping-details');

      this.productJson = JSON.parse(this.getAttribute('data-product-json'));

      this.hasUnlistedVariants = (this.productJson.variantsCount || 0) > this.productJson.variants.length;

      if (
        this.productJson.selectedVariant &&
        !this.productJson.variants.some((variant) => variant.id === this.productJson.selectedVariant.id)
      ) {
        this.productJson.variants.push(this.productJson.selectedVariant);
      }

      this.currentVariant = null;

      this.preOrderMutationObserver();

      // Initialize addon tracking
      this.selectedAddons = new Set();
      this.initializeAddonTracking();

      // Cache variants for performance
      this.variants = {};
      for (const variant of this.productJson.variants) {
        this.variants[variant.id] = {
          options: variant.options,
          availability: variant.available,
        };
      }

      if (this.form) {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      if (this.options?.length) {
        this.options.forEach((option) => {
          option.addEventListener('click', this.handleOptionClick.bind(this));
        });
      }

      // Set up initial option availability states based on server-selected variant
      this.initializeOptionAvailability();
    }

    initializeOptionAvailability() {
      // Get the variant ID from the server-rendered state
      const variantId = this.variantIdInput?.value;

      if (!variantId || !this.productJson.variants) return;

      // Find the variant object from the ID
      const currentVariant = this.productJson?.variants?.find((v) => v.id == variantId);

      if (!currentVariant) return;

      this.currentVariant = currentVariant;

      if (window.location.href.indexOf('?variant') === -1) {
        this.removeInitialSelection();
        this.updateAddToCartAvailability(currentVariant);
      } else {
        if (!this.hasUnlistedVariants) {
          this.updateAllOptionStatus(currentVariant);
        }

        this.dispatchEvent(
          new CustomEvent('variantOptionsChanged', {
            detail: { variant: currentVariant },
            bubbles: true,
          })
        );
      }
    }

    removeInitialSelection() {
      const optionGroupLabels = this.querySelectorAll('[data-option-group-label]');
      const options = this.querySelectorAll('[data-option]');
      optionGroupLabels.forEach((label) => label.innerText = '');
      options.forEach((option) => {
        option.classList.remove('unavailable');
        option.classList.add('requires-selection');
      });
    }

    preOrderMutationObserver() {
      const preOrderWrapper = this.querySelector('[data-stoq-pre-order-wrapper]');

      if (!preOrderWrapper) return;

      const observer = new MutationObserver(() => {
        this.setPreOrderText();
      });

      observer.observe(preOrderWrapper, {
        childList: true
      });
    }

    async handleOptionClick(e) {
      e.preventDefault();
      const clickedOption = e.currentTarget;
      const optionPosition = clickedOption.dataset.optionPosition;
      const optionValue = clickedOption.dataset.option;

      // Update option selection
      this.updateOptionSelection(clickedOption, optionPosition);
      this.updateOptionLabel(optionValue, clickedOption);

      // Get current selection and find matching variant
      const selectedOptions = this.getSelectedOptions();
      const selectedOptionValues = selectedOptions.map((option) => option.dataset.option);

      let variant;

      if (this.hasUnlistedVariants) {
        this._lastBtnState = null;
        if (this.submitButton) this.submitButton.disabled = true;
        this.setAttribute('aria-busy', 'true');

        this._optionRequestId = (this._optionRequestId || 0) + 1;
        const requestId = this._optionRequestId;

        try {
          variant = await this.fetchVariantFromServer();
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Server variant lookup failed, falling back to client-side matching:', error);
          variant = this.findMatchingVariant(selectedOptionValues);
        } finally {
          if (requestId === this._optionRequestId) {
            this.removeAttribute('aria-busy');
          }
        }
      } else {
        variant = this.findMatchingVariant(selectedOptionValues);
      }

      this.currentVariant = variant;

      // Update all UI elements
      this.updateVariantId(variant);
      this.updatePrice(variant);
      this.updateSku(variant);
      this.updateAddToCartAvailability(variant);
      this.updateInventoryGauge(variant);

      if (this.stickyAtc && !this.bundleForm) {
        this.stickyAtc.updateVariantTitle(variant);
        this.stickyAtc.updateVariantPrice(variant);
      }

      // Update OOS state for all option groups
      if (!this.hasUnlistedVariants) {
        this.updateAllOptionStatus(variant);
      }

      // Dispatch custom event for other components (like product gallery)
      if (variant) {
       const variationId = variant.sku || variant.id;

        if (this.hasAttribute('data-cnstrc-item-variation-id')) {
          this.setAttribute('data-cnstrc-item-variation-id', variationId);
        }

        if (this.hasAttribute('data-cnstrc-item-price')) {
          this.setAttribute('data-cnstrc-item-price', (variant.price / 100).toFixed(2));
        }

        this.setAttribute('data-selected-variant-id', variationId);
        this.setAttribute('data-selected-variant-price', (variant.price / 100).toFixed(2));

        this.dispatchEvent(
          new CustomEvent('variantOptionsChanged', {
            detail: { variant, selectedOptions: selectedOptionValues },
            bubbles: true,
          })
        );

        // Refresh Affirm when variant changes
        this.priceUpdateEventHandler(variant.price);

        if (variant.available) {
          this.classList.remove('soldout');
        } else {
          this.classList.add('soldout');
        }

        const maxQuantity = variant.inventory_quantity >= 1 ? variant.inventory_quantity : 1;
        this.setAttribute('data-max-quantity', maxQuantity);

        if (this.quantityAmount) {
          this.quantityAmount.setAttribute('max', maxQuantity);

          if (this.quantityAmount.value > maxQuantity) {
            this.quantityAmount.value = maxQuantity;
          }
        }
      } else {
        this.dispatchEvent(new CustomEvent('variantUnavailable'));

        if (this.price) {
          this.price.classList.remove('text-red');
        }

        this.classList.add('soldout');
      }

      if (this.productShippingDetails) {
        this.productShippingDetails.reload(variant);
      }

      document.dispatchEvent(new CustomEvent('buildLocationsList'));
    }

    updateAllOptionStatus(selectedVariant) {
      // Clear all unavailable states first
      this.options.forEach((option) => option.classList.remove('unavailable'));

      // Get current selections to determine what's available
      const selectedOptions = this.getSelectedOptions();
      const selectedOptionValues = selectedOptions.map((option) => option.dataset.option);

      // For each option group, determine which options should be unavailable
      const optionGroups = this.getOptionGroups();

      optionGroups.forEach((groupOptions, groupIndex) => {
        const groupPosition = groupIndex + 1;

        groupOptions.forEach((option) => {
          const optionValue = option.dataset.option;

          // Check if this option value is available in any variant with current selections
          const isAvailable = this.isOptionAvailable(optionValue, groupPosition, selectedVariant, selectedOptionValues);

          if (!isAvailable) {
            option.classList.add('unavailable');
          }
        });
      });
    }

    getOptionGroups() {
      const groups = [];
      const maxPosition = Math.max(...Array.from(this.options).map((o) => parseInt(o.dataset.optionPosition)));

      for (let i = 1; i <= maxPosition; i++) {
        groups.push(Array.from(this.options).filter((o) => o.dataset.optionPosition === i.toString()));
      }

      return groups;
    }

    isOptionAvailable(optionValue, optionPosition, selectedVariant, selectedOptionValues) {
      // Check if there's any variant that has this option value and is available
      // while keeping other selected options the same
      return this.productJson.variants.some((variant) => {
        // Check if this variant has the option value in the right position
        if (variant.options[optionPosition - 1] !== optionValue) {
          return false;
        }

        // If we have a selected variant, check compatibility with other selected options
        if (selectedVariant) {
          const otherSelectedOptions = selectedVariant.options.filter((_, index) => index !== optionPosition - 1);
          const otherVariantOptions = variant.options.filter((_, index) => index !== optionPosition - 1);
          return otherSelectedOptions.every((option) => otherVariantOptions.includes(option)) && variant.available;
        }

        // If no variant is selected, check if this option can work with current selections
        // by ensuring all currently selected options (except the one we're testing) are compatible
        const otherSelectedOptions = selectedOptionValues.filter((_, index) => index !== optionPosition - 1);
        const otherVariantOptions = variant.options.filter((_, index) => index !== optionPosition - 1);

        return otherSelectedOptions.every((option) => otherVariantOptions.includes(option)) && variant.available;
      });
    }

    updateOptionSelection(clickedOption, optionPosition) {
      // Remove selected and unavailable classes from all options in this group
      this.options.forEach((option) => {
        if (option.dataset.optionPosition === optionPosition) {
          option.classList.remove('selected', 'requires-selection');
          option.setAttribute('aria-checked', 'false');
        }
      });

      // Add selected class to clicked option and set aria-checked to true
      clickedOption.classList.add('selected');
      clickedOption.setAttribute('aria-checked', 'true');
    }

    updateOptionLabel(optionValue, clickedOption) {
      const labelValue = clickedOption.closest('fieldset').querySelector('[data-option-group-label]');
      if (labelValue) {
        labelValue.textContent = optionValue;
      }
    }

    updatePrice(variant) {
      if (!this.price) return;

      if (!variant || typeof variant.price !== 'number') {
        if (this.compareAtPrice) this.compareAtPrice.classList.add('hidden');
        return;
      }

      const priceCents = variant.price;
      const compareCents = variant.comparePrice ?? variant.compare_at_price;

      if (this.price) {
        this.price.textContent = window.formatMoney(priceCents);
      }

      const showCompare = typeof compareCents === 'number' && compareCents > priceCents;

      if (this.compareAtPrice) {
        if (showCompare) {
          this.compareAtPrice.textContent = window.formatMoney(compareCents);
          this.compareAtPrice.classList.remove('hidden');
          this.compareAtPrice.setAttribute('aria-hidden', 'false');

          if (this.price) {
            this.price.classList.add('text-red');
          }
        } else {
          this.compareAtPrice.classList.add('hidden');
          this.compareAtPrice.setAttribute('aria-hidden', 'true');

          if (this.price) {
            this.price.classList.remove('text-red');
          }
        }
      }
    }

    updateSku(variant) {
      if (!this.sku) return;
      const val = variant?.sku ?? 'N/A';
      this.sku.textContent = val;
    }

    updateInventoryGauge(variant) {
      if (!this.inventoryMessage) {
        return;
      }

      const inventory = variant?.inventory_quantity || 0;
      const sellOutRisk = this.calculateSellOutRisk(inventory);

      if (sellOutRisk === 'Low') {
        this.inventoryGauge.classList.add('hidden');
      } else {
        this.inventoryGauge.classList.remove('hidden');
      }

      this.inventoryMessage.textContent = `${sellOutRisk} (${inventory} remaining)`;
    }

    calculateSellOutRisk(inventory) {
      if (inventory === 0) {
        this.inventoryIcon.innerHTML = this.productJson.high_risk_icon;
        return 'Sold Out';
      } else if (inventory <= this.highRiskThreshold) {
        this.inventoryIcon.innerHTML = this.productJson.high_risk_icon;
        return 'High';
      } else if (inventory <= this.mediumRiskThreshold) {
        this.inventoryIcon.innerHTML = this.productJson.medium_risk_icon;
        return 'Medium';
      } else {
        return 'Low';
      }
    }

    getSelectedOptions() {
      return Array.from(this.options).filter((option) => option.classList.contains('selected'));
    }

    getSelectedOptionValueIds() {
      const idsByPosition = new Map();

      this.getSelectedOptions().forEach((option) => {
        const position = parseInt(option.dataset.optionPosition, 10);
        if (option.dataset.optionValueId) {
          idsByPosition.set(position, option.dataset.optionValueId);
        }
      });

      return Array.from(idsByPosition.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([, id]) => id);
    }

    async fetchVariantFromServer() {
      const valueIds = this.getSelectedOptionValueIds();

      if (!valueIds.length) return null;

      this.serverRequestController?.abort();
      this.serverRequestController = new AbortController();

      const url = new URL(this.productJson.url, window.location.origin);
      url.searchParams.set('option_values', valueIds.join(','));
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString(), { signal: this.serverRequestController.signal });

      if (!response.ok) {
        throw new Error(`Section render request failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      this.syncOptionStatesFromDocument(doc);

      const buybox = doc.querySelector('[data-product-json]');
      if (!buybox) return null;

      const productJson = JSON.parse(buybox.getAttribute('data-product-json'));
      return productJson.selectedVariant ?? null;
    }

    syncOptionStatesFromDocument(doc) {
      this.options.forEach((option) => {
        const valueId = option.dataset.optionValueId;
        if (!valueId) return;

        const counterpart = doc.querySelector(`[data-option-value-id="${valueId}"]`);

        if (!counterpart) {
          option.classList.add('unavailable');
          return;
        }

        option.classList.toggle('unavailable', counterpart.classList.contains('unavailable'));
        option.classList.toggle('hidden', counterpart.classList.contains('hidden'));
      });
    }

    findMatchingVariant(selectedOptionValues) {
      if (!this.productJson.variants) {
        return null;
      }

      for (const variant of this.productJson.variants) {
        if (variant.options.every((option) => selectedOptionValues.includes(option))) {
          return variant;
        }
      }
      return null;
    }

    updateVariantId(variant) {
      if (variant) {
        this.variantIdInput.value = variant.id;
      }
    }

    validateOptionSelection() {
      const selectedOptions = this.querySelectorAll('[data-option].requires-selection');
      return selectedOptions && selectedOptions.length == 0;
    }

    updateAddToCartAvailability(variant) {
      const available = !!variant?.available;
      const chooseOptionsText = this.submitButton?.getAttribute('data-choose-options-text') ?? 'Choose Options';
      const unavailableText = this.submitButton?.getAttribute('data-unavailable-text') ?? 'Unavailable';
      const availableText = this.submitButton?.getAttribute('data-available-text') ?? 'Add to cart';
      const soldOutText = this.submitButton?.getAttribute('data-soldout-text') ?? 'Sold out';
      const label = !variant ? unavailableText : available ? availableText : soldOutText;
      const disabled = !available;
      const optionsSelected = this.validateOptionSelection();

      if (!optionsSelected) {
        this.updateButton(chooseOptionsText, true);
        this._lastBtnState = { label, disabled: true };
        return;
      }

      if (this._lastBtnState?.label === label && this._lastBtnState?.disabled === disabled) return;

      this.updateButton(label, disabled);
      this._lastBtnState = { label, disabled };
    }

    priceUpdateEventHandler(newPrice) {
      this.changePriceHandler(newPrice);
    }

    changePriceHandler(newPrice) {
      this.updateAffirmPromos(newPrice);
    }

    updateAffirmPromos(newPrice) {
      //Update the wrapper elements' attributes in the DOM
      const affirmElements = this.querySelectorAll('.affirm-as-low-as[data-amount]');
      if (!affirmElements.length) return;

      affirmElements.forEach((element) => {
        element.setAttribute('data-amount', newPrice);
      });

      // Call affirm.ui.refresh to re-render the promotional messaging components
      if (window.affirm?.ui?.refresh) {
        window.affirm.ui.refresh();
      }
    }

    async handleSubmit(e) {
      e.preventDefault();

      // Prevent double submission
      if (this.submitButton.disabled) return;

      this.updateButton(this.submitButton.getAttribute('data-adding-text'), true);

      const shopifyBundleId = this.querySelector('[data-shopify-bundle-id]');

      if (shopifyBundleId) {
        shopifyBundleId.value = crypto.randomUUID();
      }

      const formData = new FormData(this.form);
      const atcData = Object.fromEntries(formData);
      const propertiesObject = Object.keys(atcData)
        .filter((key) => key.includes('properties'))
        .reduce((prev, cur) => {
          const name = cur.replace(/properties|\[|\]/g, '');
          if (name.length === 0 || !atcData[cur] || atcData[cur].length === 0) return prev;
          prev[name] = atcData[cur];
          return prev;
        }, {});

      try {
        // Get main product data
        const mainItem = {
          id: parseInt(this.variantIdInput.value),
          quantity: parseInt(this.quantityAmount?.value || 1),
          properties: propertiesObject,
        };

        // Get selected addons
        const addonItems = this.getSelectedAddons();
        const recommendationItems = this.getSelectedRecommendations();

        // Combine main product with addons
        const formData = {
          items: [mainItem, ...addonItems, ...recommendationItems],
        };

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });

        if (response.ok) {
          const result = await response.json();
          this.handleSuccess(result);
        } else {
          const errorData = await response.json();
          this.handleError(errorData);
        }
      } catch (error) {
        console.error('Cart add error:', error);
        this.handleError(error);
      }
    }

    handleSuccess() {
      const sideCart = document.querySelector('side-cart');

      this.updateButton(this.submitButton.getAttribute('data-success-text'), false);

      this.updateCartBubbles();

      if (sideCart) {
        sideCart.renderContents();
      }

      setTimeout(() => {
        this.updateButton(this.getOriginalButtonText(), false);

        if (sideCart) {
          sideCart.open();
        }
      }, 1500);
    }

    handleError(error) {
      console.error('Failed to add to cart:', error);

      this.updateButton(this.submitButton.getAttribute('data-error-text'), false);

      setTimeout(() => {
        this.updateButton(this.getOriginalButtonText(), false);
      }, 1500);
    }

    updateButton(text, disabled) {
      if (this.submitButton) {
        this.submitButton.textContent = text;
        this.submitButton.disabled = disabled;

        if (disabled) {
          this.submitButton.setAttribute('aria-disabled', 'true');
        } else {
          this.submitButton.removeAttribute('aria-disabled');
        }
      }

      if (this.stickyAtc && !this.preOrderForm) {
        this.stickyAtc.updateSubmitButton(text, disabled);
      }
    }

    setPreOrderText() {
      setTimeout(() => {
        const optionsSelected = this.validateOptionSelection();

        if (!optionsSelected) {
          return;
        }

        const isSoldout = this.submitButton.hasAttribute('disabled');
        const stoqWidget = this.querySelector('.restock-rocket-preorder-description');
        let buttonText = this.submitButton.getAttribute('data-available-text');

        const preOrderPropertyInput = this.form.querySelector('[name="properties[_pre-order]"]');

        if (preOrderPropertyInput) {
          preOrderPropertyInput.remove();
        }

        if (isSoldout) {
          buttonText = this.submitButton.getAttribute('data-soldout-text');
        } else if (stoqWidget) {
          buttonText = this.submitButton.getAttribute('data-pre-order-text');
          const preOrderPropertyInput = document.createElement('input');
          preOrderPropertyInput.setAttribute('type', 'hidden');
          preOrderPropertyInput.setAttribute('name', 'properties[_pre-order]');
          preOrderPropertyInput.setAttribute('value', 'true');
          this.form.appendChild(preOrderPropertyInput);

          const variantId = this.variantIdInput?.value;
          const currentVariant =
            this.currentVariant && this.currentVariant.id == variantId
              ? this.currentVariant
              : this.productJson.variants.find((variant) => variant.id == variantId);
          const quantitySelectorInput = this.querySelector('product-buybox-quantity-selector [data-quantity-input]');

          if (currentVariant && currentVariant?.pre_order_inventory && quantitySelectorInput) {
            quantitySelectorInput.setAttribute('max', currentVariant.pre_order_inventory);
          }
        }

        if (this.submitButton) {
          this.submitButton.innerText = buttonText;
        }

        if (this.stickyAtc) {
          this.stickyAtc.updateSubmitButton(buttonText, isSoldout);
        }
      }, 500);
    }

    getOriginalButtonText() {
      // Check if product is available
      const form = this.form;
      if (form) {
        // TODO: You might want to check variant availability here
        // For now, return default text
        // const variantId = this.variantIdInput.value;
        return this.submitButton?.getAttribute('data-available-text') ?? 'Add to cart';
      }
      return this.submitButton?.getAttribute('data-available-text') ?? 'Add to cart';
    }

    async updateCartBubbles() {
      try {
        // Fetch current cart to get updated count
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        if (response.ok) {
          const cart = await response.json();
          const cartBubbles = document.querySelectorAll('[data-cart-bubble]');

          cartBubbles.forEach((bubble) => {
            bubble.classList.remove('hidden');
            bubble.textContent = cart.item_count;

            // Show bubble if cart was empty before
            if (cart.item_count > 0) {
              bubble.style.display = '';
            }
          });
        }
      } catch (error) {
        console.error('Failed to update cart bubbles:', error);
      }
    }

    initializeAddonTracking() {
      // Only initialize if addon elements exist within this product-buybox
      const addonSelects = this.querySelectorAll('select[id^="addon-variant-"]');
      const addonDetails = this.querySelectorAll('.addon-item details');

      if (addonSelects.length === 0 && addonDetails.length === 0) {
        return; // No addons present, skip initialization
      }

      // Handle variant selection changes - scoped to this product-buybox
      addonSelects.forEach((select) => {
        select.addEventListener('change', () => {
          const addonItem = select.closest('.addon-item');
          addonItem.dataset.selectedAddonVariant = select.value;
          this.updateAddonVariant(addonItem, select.value);
        });
      });

      // Handle details toggle (add/remove from cart) - scoped to this product-buybox
      addonDetails.forEach((details) => {
        details.addEventListener('toggle', () => {
          const addonItem = details.closest('.addon-item');
          const productId = addonItem.dataset.productId;

          if (details.open) {
            const variantSelect = addonItem.querySelector('select[id^="addon-variant-"]');
            const variantId = variantSelect?.options[0]?.value || addonItem.dataset.addonVariantId;
            addonItem.dataset.selectedAddonVariant = variantId;
            this.selectedAddons.add(productId);
            if (variantSelect) this.updateAddonVariant(addonItem, variantId);
          } else {
            this.selectedAddons.delete(productId);
          }
        });
      });
    }

    getSelectedRecommendations() {
      const items = [];
      const selectedRecommendations = this.querySelectorAll('buy-box-recommendations-product-card.active');

      // Only process if we have selected recommendations
      if (!selectedRecommendations.length) {
        return items;
      }

      selectedRecommendations.forEach((product) => {
        if (product.classList.contains('unavailable')) return;

        const variantId = product.getAttribute('data-selected-variant-id');

        if (variantId) {
          items.push({ id: parseInt(variantId), quantity: 1 });
        }
      });

      return items;
    }

    getSelectedAddons() {
      const addonItems = [];

      // Only process if we have selected addons
      if (!this.selectedAddons || this.selectedAddons.size === 0) {
        return addonItems;
      }

      this.selectedAddons.forEach((productId) => {
        const addonItem = this.querySelector(`[data-product-id="${productId}"]`);
        if (addonItem) {
          const selectedVariant = addonItem.dataset.selectedAddonVariant;
          if (selectedVariant) {
            addonItems.push({ id: parseInt(selectedVariant), quantity: 1 });
          }
        }
      });

      return addonItems;
    }

    updateAddonVariant(addonItem, variantId) {
      const selectedOption = addonItem.querySelector(`select[id^="addon-variant-"] option[value="${variantId}"]`);
      if (!selectedOption) return;

      const priceCents = parseInt(selectedOption.dataset.price);
      const compareCents = selectedOption.dataset.comparePrice ? parseInt(selectedOption.dataset.comparePrice) : null;
      const variantImageUrl = selectedOption.dataset.image;

      const priceElement = addonItem.querySelector('[data-addon-price]');
      const comparePriceElement = addonItem.querySelector('[data-addon-compare-price]');
      const imageElement = addonItem.querySelector('[data-addon-image]');

      if (!priceElement) return;

      // Update price
      priceElement.textContent = window.formatMoney(priceCents);

      // Update image
      if (imageElement && variantImageUrl) {
        imageElement.src = variantImageUrl;
        imageElement.srcset = `${variantImageUrl} 1x, ${variantImageUrl.replace('width=96', 'width=192')} 2x`;
      }

      // Handle compare price
      const showCompare = compareCents && compareCents > priceCents;
      if (comparePriceElement) {
        comparePriceElement.style.display = showCompare ? '' : 'none';
        comparePriceElement.textContent = showCompare ? window.formatMoney(compareCents) : '';
        priceElement.classList.toggle('text-red', showCompare);
      } else if (showCompare) {
        const newCompareElement = document.createElement('del');
        newCompareElement.className = 'text-sm';
        newCompareElement.setAttribute('data-addon-compare-price', '');
        newCompareElement.textContent = window.formatMoney(compareCents);
        priceElement.classList.add('text-red');
        priceElement.parentNode.insertBefore(newCompareElement, priceElement.nextSibling);
      }
    }
  }

  customElements.define('product-buybox', ProductBuybox);
}

if (!customElements.get('product-buybox-quantity-selector')) {
  class ProductBuyboxQuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.quantityAmount = this.querySelector('[data-quantity-input]');
      this.quantityActions = this.querySelectorAll('[data-action]');
    }

    connectedCallback() {
      if (this.quantityActions?.length) {
        this.quantityActions.forEach((action) => {
          action.addEventListener('click', this.updateQuantity.bind(this));
        });
      }
    }

    updateQuantity(e) {
      const action = e.currentTarget.dataset.action;

      if (!action) {
        return;
      } else if (action === 'add') {
        const newValue = parseInt(this.quantityAmount.value) + 1;
        const maxQuantity = parseInt(this.quantityAmount.getAttribute('max'));

        if (newValue <= maxQuantity) {
          this.quantityAmount.value++;
        }
      } else {
        if (this.quantityAmount.value > 1) {
          this.quantityAmount.value--;
        } else {
          return;
        }
      }
    }
  }

  customElements.define('product-buybox-quantity-selector', ProductBuyboxQuantitySelector);
}

if (!window.customElements.get('product-buybox-colors')) {
  class ProductBuyboxColors extends HTMLElement {
    constructor() {
      super();
      this.buyBox = this.closest('product-buybox');
      this.variants = this.getAttribute('data-variants') ? JSON.parse(this.getAttribute('data-variants')) : null;
      this.hasBeenSorted = false;
    }

    connectedCallback() {
      this.buyBox.addEventListener('variantOptionsChanged', () => this.sortColors());
      this.buyBox.addEventListener('variantUnavailable', () => this.sortColors());
      this.sortColors();
    }

    sortColors() {
      const priceStatusArray = [...new Set(this.variants.map((variant) => variant.price_status))]?.filter((item) => item != '');

      if (!priceStatusArray.length && !this.hasBeenSorted) return;

      const selectedOptions = this.buyBox.querySelectorAll('[data-text-option][aria-checked="true"]');
      const selectedOptionsFormatted = Array.from(selectedOptions)
        .map((option) => option.innerText)
        .join(' / ');
      const variantsWithPriceStatus = this.variants.filter(
        (variant) => variant.title.indexOf(selectedOptionsFormatted) >= 0 && variant.price_status != ''
      );
      const colorOptions = this.buyBox.querySelectorAll('[data-color-option]');
      const parentFieldsetWrapper = this.closest('[data-color-fieldset-wrapper]');

      this.hasBeenSorted = true;
      this.innerHTML = '';
      this.classList.remove('flex');
      priceStatusArray.unshift('No status');

      if (parentFieldsetWrapper && variantsWithPriceStatus.length) {
        parentFieldsetWrapper.classList.add('border-y', 'border-light-gray-3', 'py-3');
      } else {
        parentFieldsetWrapper.classList.remove('border-y', 'border-light-gray-3', 'py-3');
      }

      // Create status divs for swatch sorting
      priceStatusArray.forEach((priceStatus) => {
        const priceStatusDiv = document.createElement('div');

        priceStatusDiv.setAttribute('data-status-wrapper', '');

        priceStatusDiv.innerHTML = `
          ${
            priceStatus != 'No status'
              ? `
              <strong class="text-xs text-red [*:not(:has(:first-child:last-child))>&]:hidden">
                ${priceStatus}
              </strong>
            `
              : ''
          }

          <div class="mb-3 empty:hidden flex flex-row gap-2 flex-wrap" data-status="${priceStatus}"></div>
        `;

        this.appendChild(priceStatusDiv);
      });

      // Sort swatches into their status divs
      colorOptions.forEach((option) => {
        const colorName = option.getAttribute('data-option');
        const matchingVariant = variantsWithPriceStatus.find((variant) => variant.options.includes(colorName));
        const matchingStatusDiv = this.querySelector(`[data-status="${matchingVariant?.price_status}"]`);
        const noStatusDiv = this.querySelector('[data-status="No status"]');

        if (matchingVariant && matchingStatusDiv) {
          matchingStatusDiv.appendChild(option);
        } else if (noStatusDiv) {
          noStatusDiv.appendChild(option);
        }
      });

      // Check for no visible swatches
      const statusWrappers = document.querySelectorAll('[data-status-wrapper]');

      statusWrappers.forEach((wrapper) => {
        const colorOptions = wrapper.querySelectorAll('[data-color-option]');
        const visibleColorOptions = colorOptions && colorOptions.length ? Array.from(colorOptions).filter((option) => !option.classList.contains('hidden')) : [];

        if (visibleColorOptions.length) return;

        wrapper.classList.add('hidden');
      });
    }
  }

  window.customElements.define('product-buybox-colors', ProductBuyboxColors);
}
